import type { Dict } from './types';

export const en: Dict = {
  'splash.loading': 'Loading…',
  'splash.subtitle': 'Preparing your pet',
  'error.generic.title': 'Something went wrong',
  'error.generic.body': 'Try reloading the page.',
  'error.generic.button': 'Reload',
  'save.fallback': 'Offline save unavailable — using session storage.',
  'save.session-only': 'Could not save. Progress may be lost on reload.',
  'stage.egg': 'Egg',
  'stage.baby': 'Baby',
  'stage.teen': 'Teen',
  'stage.adult': 'Adult',
  'stage.elder': 'Elder',
  'stage.dead': 'Deceased',
  'action.feed': 'Feed',
  'action.play': 'Play',
  'action.clean': 'Clean',
  'action.discipline': 'Discipline',
  'action.heal': 'Heal',
  'action.light': 'Light',
  'action.minigame': 'Games',
  'feedback.refused.feed': 'Cannot feed yet',
  'feedback.refused.play': 'Cannot play yet',
  'feedback.refused.clean': 'Nothing to clean',
  'feedback.refused.discipline': 'Discipline will not help now',
  'feedback.refused.heal': 'Cannot heal yet',
  'feedback.refused.feed.cooldown': 'Easy, it just ate',
  'feedback.refused.play.cooldown': 'It needs a breather',
  'feedback.refused.heal.cooldown': 'Medicine is recharging',
  'feedback.refused.clean.notNeeded': 'The litter box is already clean',
  'feedback.refused.discipline.notNeeded': 'It is not throwing a tantrum',
  'feedback.refused.heal.notNeeded': 'It is not sick',
  'feedback.refused.tantrum': 'It is throwing a tantrum',
  'feedback.success.feed': 'Nom!',
  'feedback.success.play': 'It liked that!',
  'feedback.success.clean': 'All clean',
  'feedback.success.heal': 'Much better',
  'feedback.success.discipline': 'Tantrum resolved',
  'feedback.need.feed': 'It is hungry',
  'feedback.need.play': 'It wants attention',
  'feedback.need.clean': 'Time to clean the litter box',
  'feedback.need.heal': 'It got sick',
  'food.meal.name': 'Meal',
  'food.meal.short': '+Hunger',
  'food.meal.desc': 'Regular food: strong hunger recovery, low weight gain.',
  'food.snack.name': 'Snack',
  'food.snack.short': '+Happy',
  'food.snack.desc': 'Treat: strong happiness gain, more weight.',
  'food.fruit.name': 'Fruit',
  'food.fruit.short': 'Light',
  'food.fruit.desc': 'Balanced food: light hunger and happiness gain.',
  'food.protein.name': 'Protein',
  'food.protein.short': 'Hearty',
  'food.protein.desc': 'Heavy food: strong hunger gain, more weight.',
  'toy.ball.name': 'Ball',
  'toy.ball.short': 'Active',
  'toy.ball.desc': 'Runs and hops after the ball; reduces more weight.',
  'toy.rope.name': 'Rope',
  'toy.rope.short': 'Training',
  'toy.rope.desc': 'Active play that also trains discipline.',
  'toy.plush.name': 'Plush',
  'toy.plush.short': 'Comfort',
  'toy.plush.desc': 'Calms down and grants plenty of happiness.',
  'toy.puzzle.name': 'Puzzle',
  'toy.puzzle.short': 'Focus',
  'toy.puzzle.desc': 'Encourages curiosity and discipline.',
  'status.dead': 'died',
  'status.sleeping': 'sleeping',
  'status.tantrum': 'tantrum',
  'status.sick': 'sick',
  'behavior.mood.hungry': 'hungry',
  'behavior.mood.lonely': 'wants attention',
  'behavior.mood.dirty': 'uncomfortable',
  'behavior.mood.sleepy': 'sleepy',
  'behavior.mood.bored': 'bored',
  'behavior.mood.overfed': 'heavy',
  'tooltip.feed': 'Feed (raise hunger)',
  'tooltip.play': 'Play (raise happiness)',
  'tooltip.clean': 'Clean poop',
  'tooltip.discipline': 'Discipline only during tantrums; using it without cause lowers happiness.',
  'tooltip.heal': 'Apply medicine dose',
  'tooltip.light': 'Toggle light',
  'tooltip.hunger': 'Hunger — feed when low.',
  'tooltip.happiness': 'Happiness — play when low.',
  'tooltip.health': 'Health — derived from hunger, happiness, weight, mess and disease.',
  'tooltip.discipline.bar': 'Discipline — high makes the pet more patient.',
  'tooltip.weight': 'Weight — affects speed and look.',
  'topbar.locale': 'Switch language',
  'topbar.settings': 'Settings',
  'topbar.bestiary': 'Bestiary',
  'topbar.breeding': 'Breed',
  'topbar.lineage': 'Lineage',
  'topbar.profile': 'Profile',
  'topbar.reset': 'Reset progress',
  'careProfile.title': 'Care profile',
  'careProfile.currentTraits': 'Current traits',
  'careProfile.tendencies': 'Tendencies',
  'careProfile.story': 'Care story',
  'careProfile.noTraits': 'No trait has settled in yet.',
  'careProfile.close': 'Close',
  'careProfile.temperament': 'Temperament',
  'careProfile.summary.calm': 'Patient and steady in everyday routines.',
  'careProfile.summary.needy': 'Values company and frequent attention.',
  'careProfile.summary.playful': 'Finds fun in almost every interaction.',
  'careProfile.summary.glutton': 'Takes a strong interest in food and rewards.',
  'careProfile.summary.stubborn': 'Has strong opinions and intense reactions.',
  'careProfile.summary.fragile': 'Sensitive and strongly shaped by care.',
  'careProfile.summary.tidy': 'Prefers routine, cleanliness, and order.',
  'careProfile.trait.well-fed': 'Well fed',
  'careProfile.trait.spoiled': 'Spoiled',
  'careProfile.trait.playful': 'Playful',
  'careProfile.trait.messy': 'Messy',
  'careProfile.trait.resilient': 'Resilient',
  'careProfile.trait.disciplined': 'Disciplined',
  'careProfile.trait.rebellious': 'Rebellious',
  'careProfile.trait.heavy': 'Sturdy',
  'careProfile.trait.frail': 'Frail',
  'careProfile.trait.athletic': 'Athletic',
  'careProfile.trait.comfort-seeker': 'Comfort seeker',
  'careProfile.trait.curious': 'Curious',
  'careProfile.tendency.discovering': 'Still showing its preferences.',
  'careProfile.tendency.balancedDiet': 'Balanced diet',
  'careProfile.tendency.sweetTooth': 'Taste for treats',
  'careProfile.tendency.activePlay': 'Prefers active play',
  'careProfile.tendency.comfortPlay': 'Prefers comfort and company',
  'careProfile.tendency.focusPlay': 'Prefers focus and challenges',
  'careProfile.story.new': 'Its care story is only beginning.',
  'careProfile.story.nutritious': 'Gets more nutritious meals than treats.',
  'careProfile.story.treats': 'Gets more treats than nutritious meals.',
  'careProfile.story.lateCleaning': 'Some cleanups happened later than ideal.',
  'careProfile.story.cleanHome': 'Its space is usually cleaned when needed.',
  'careProfile.story.goodDiscipline': 'Tantrums are corrected at the right time.',
  'careProfile.story.mistakes': 'Some care was offered at the wrong time.',
  'careProfile.story.recovered': 'Has received treatment and recovered from illness.',
  'settings.title': 'Settings',
  'settings.locale': 'Language',
  'settings.audio.master': 'Master volume',
  'settings.audio.sfx': 'SFX',
  'settings.audio.ambient': 'Ambient',
  'settings.audio.music': 'Music',
  'settings.audio.muted': 'Muted',
  'settings.a11y.fontScale': 'Font size',
  'settings.a11y.reducedMotion': 'Reduce motion',
  'settings.fastMode': 'Fast mode (dev)',
  'settings.cloud.enabled': 'Cloud sync',
  'settings.close': 'Close',
  'settings.reset': 'Reset progress',
  'settings.reset.confirm': 'Reset wipes your pet, NPCs and lineage. Confirm?',
  'bestiary.title': 'Bestiary',
  'bestiary.discovered': '{n} of 63 discovered',
  'bestiary.unknown': 'Unknown — discover by breeding creatures.',
  'bestiary.group.singular': 'type',
  'bestiary.group.plural': 'types',
  'bestiary.close': 'Close',
  'breeding.title': 'Breeding',
  'breeding.canBreed': '{name} is ready to breed.',
  'breeding.noPartners': 'No family partner available — {name} will breed with a wild stranger.',
  'breeding.choosePartner': "Choose a partner from {name}'s family:",
  'breeding.partnerRandom': 'Wild stranger',
  'breeding.cannotBreed': '{name} is not ready yet. Only adults and elders can breed.',
  'breeding.success': 'Offspring generated! Parents retired as NPCs.',
  'breeding.inheritance.title': "{name}'s inheritance",
  'breeding.inheritance.parents': 'Child of {a} and {b}',
  'breeding.inheritance.close': 'Continue with the child',
  'breeding.inheritance.origin.parent-a': 'From {a}',
  'breeding.inheritance.origin.parent-b': 'From {b}',
  'breeding.inheritance.origin.both': 'Blend of both',
  'breeding.inheritance.origin.variation': 'New variation',
  'breeding.inheritance.locus.body': 'Body',
  'breeding.inheritance.locus.ears': 'Ears',
  'breeding.inheritance.locus.horns': 'Horns',
  'breeding.inheritance.locus.tail': 'Tail',
  'breeding.inheritance.locus.markings': 'Markings',
  'breeding.inheritance.locus.eggPattern': 'Egg',
  'breeding.inheritance.locus.paletteHue': 'Colors',
  'breeding.inheritance.locus.primaryTypes': 'Elements',
  'breeding.inheritance.locus.inheritedTraits': 'Inherited traits',
  'breeding.failure': 'Could not breed: {reason}.',
  'breeding.confirm': 'Breed',
  'breeding.cancel': 'Cancel',
  'lineage.title': 'Lineage',
  'lineage.activeLabel': 'Active',
  'lineage.ancestors': 'Ancestors',
  'lineage.treeLabel': 'Active pet family tree',
  'lineage.role.active': 'Current pet',
  'lineage.role.parent': 'Parent',
  'lineage.role.grandparent': 'Grandparent',
  'lineage.npcsAlive': 'Living NPCs ({n})',
  'lineage.family': 'family',
  'lineage.careLegacy': 'care {score}',
  'lineage.memorials': 'Memorials ({n})',
  'lineage.cause.alive': 'alive',
  'lineage.cause.old-age': 'old age',
  'lineage.cause.neglect': 'neglect',
  'lineage.cause.expedition': 'expedition',
  'lineage.totalRegistered': '{n} creatures registered',
  'lineage.empty': 'Your lineage is empty. Breed to start.',
  'lineage.close': 'Close',
  'memorial.title': '{name} is gone',
  'memorial.cause': 'Cause: {cause}.',
  'memorial.newLife': 'New generation',
  'evolution.tier.bom': '{name} grew up strong and healthy!',
  'evolution.tier.medio': '{name} reached adulthood.',
  'evolution.tier.ruim': '{name} grew up neglected...',
  'evolution.tier.secreto': '{name} revealed a rare form!',
  'minigame.title': 'Mini-games',
  'minigame.cooldown': 'Wait {time} to play again.',
  'minigame.guess': 'Guess the side',
  'minigame.guess.instruction': 'Which hand has the treat?',
  'minigame.guess.win': 'You got it!',
  'minigame.guess.lose': 'You missed.',
  'minigame.rps': 'Rock, Paper, Scissors',
  'minigame.rps.win': 'You won!',
  'minigame.rps.lose': 'You lost.',
  'minigame.rps.draw': 'Draw.',
  'minigame.memory': 'Memory',
  'minigame.memory.complete': 'Completed!',
  'minigame.close': 'Close',
  'minigame.reward': '+{n} happiness',
  'type.Fire': 'Fire',
  'type.Water': 'Water',
  'type.Earth': 'Earth',
  'type.Air': 'Air',
  'type.Life': 'Life',
  'type.Shadow': 'Shadow',
  'biome.meadow': 'Meadow',
  'biome.forest': 'Forest',
  'biome.cave': 'Cave',
  'biome.coast': 'Coast',
  'world.title': 'World',
  'world.exitToHouse': 'Back home',
  'world.goToWorld': 'Go explore',
  'world.vitality': 'Vitality',
  'world.dayPhase.day': 'Day',
  'world.dayPhase.night': 'Night',
  'world.collected': 'Picked {item}!',
  'world.exposure.warning': 'High exposure — vitality dropping fast!',
  'world.teleportedHome': 'Vitality reached zero! Back home.',
  'world.cannotEnter.egg': 'Wait for the egg to hatch.',
  'world.cannotEnter.dead': 'The creature has passed.',
  'world.cannotEnter.lowVitality': 'Vitality too low — rest at home.',
  'inventory.title': 'Inventory',
  'inventory.empty': 'Empty. Explore the world to collect items.',
  'item.berry': 'Berry',
  'item.shell': 'Shell',
  'item.crystal': 'Crystal',
  'item.flower': 'Flower',
  'expedition.title': 'Expedition',
  'expedition.start': '⚔️ Start expedition',
  'expedition.confirm.title': 'Start expedition?',
  'expedition.confirm.body': 'The region turns dangerous. Enemies will appear. Defeat them to earn roguelite mutations.',
  'expedition.confirm.warning': 'Beware: fainting costs happiness. If sick or elder, you may die permanently.',
  'expedition.confirm.proceed': 'Let\'s go',
  'expedition.confirm.cancel': 'Not now',
  'expedition.briefing.risk': 'Affinity',
  'expedition.briefing.risk.favorable': 'favorable',
  'expedition.briefing.risk.neutral': 'neutral',
  'expedition.briefing.risk.hostile': 'hostile',
  'expedition.briefing.enemies': 'Enemies',
  'expedition.briefing.resources': 'Resources',
  'expedition.cannotStart.notReady': 'Must be adult/elder, not sick, vitality ≥ 30.',
  'expedition.extract': 'Extract',
  'expedition.extract.confirm': 'Return to world? Rare/epic mutations stay.',
  'expedition.outcome.extracted': 'Extracted!',
  'expedition.outcome.fainted': 'Fainted…',
  'expedition.outcome.died': 'The creature died on the expedition.',
  'expedition.summary.title': 'Result',
  'expedition.summary.kills': 'Enemies defeated: {n}',
  'expedition.summary.xp': 'XP gained: {n}',
  'expedition.summary.mutations': 'Mutations:',
  'expedition.summary.close': 'Close',
  'mutation.pick.title': 'Pick a mutation',
  'mutation.pick.body': 'You feel your form change.',
  'mutation.tough.name': 'Tough',          'mutation.tough.desc': '+20 max HP',
  'mutation.sharp.name': 'Sharp',          'mutation.sharp.desc': '+5 damage',
  'mutation.swift.name': 'Swift',          'mutation.swift.desc': '+20 speed',
  'mutation.quickHands.name': 'Quick hands', 'mutation.quickHands.desc': '+25% attack speed',
  'mutation.lucky.name': 'Lucky',          'mutation.lucky.desc': '+10% crit chance',
  'mutation.regen.name': 'Regen',          'mutation.regen.desc': '+1 HP/s',
  'mutation.thornedHide.name': 'Thorned hide', 'mutation.thornedHide.desc': 'Reflects 30% damage taken',
  'mutation.shield.name': 'Shield',        'mutation.shield.desc': '5s no hit = +20% absorption',
  'mutation.burnTouch.name': 'Burn touch',  'mutation.burnTouch.desc': 'Hits apply burn 4s',
  'mutation.iceTouch.name': 'Ice touch',    'mutation.iceTouch.desc': 'Hits slow enemy 2s',
  'mutation.lifesteal.name': 'Lifesteal',   'mutation.lifesteal.desc': '+20% lifesteal',
  'mutation.doubleStrike.name': 'Double strike', 'mutation.doubleStrike.desc': '25% chance of double hit',
  'mutation.fortress.name': 'Fortress',     'mutation.fortress.desc': '+50 HP, -20 speed',
  'mutation.glassCannon.name': 'Glass cannon', 'mutation.glassCannon.desc': '+15 damage, -30 HP',
  'mutation.bloodlust.name': 'Bloodlust',   'mutation.bloodlust.desc': 'After kill: +30% damage 5s',
  'mutation.phoenix.name': 'Phoenix',       'mutation.phoenix.desc': 'Resurrect 1× with 30% HP',
  'mutation.overload.name': 'Overload',     'mutation.overload.desc': 'Skill 50% cooldown, -5 HP per use',
  'mutation.aether.name': 'Aether',         'mutation.aether.desc': 'Attacks apply burn AND ice',
  'combat.skill': 'Ability',
  'combat.skill.aria': 'Elemental ability',
  'combat.attack': 'Attack',
  'combat.attack.aria': 'Attack',
  'combat.dodge.aria': 'Dodge',
  'combat.ready': 'ready',
  'combat.controls': 'Space/J/X attack · Shift/L dodge · Q/K ability',
  'combat.hp': 'HP',
  'combat.kills': 'Kills',
  'enemy.meadow-bunbun.name': 'Bunny',
  'enemy.meadow-bee.name': 'Bee',
  'enemy.meadow-bigboar.name': 'Big Boar',
  'enemy.forest-wolfpup.name': 'Wolf Pup',
  'enemy.forest-spider.name': 'Spider',
  'enemy.forest-darkspit.name': 'Darkspit',
  'enemy.cave-bat.name': 'Bat',
  'enemy.cave-rockling.name': 'Rockling',
  'enemy.cave-crystalfly.name': 'Crystal Fly',
  'enemy.coast-crabling.name': 'Crabling',
  'enemy.coast-jelly.name': 'Jelly',
  'enemy.coast-shellbeast.name': 'Shell-Beast',
  'afk.dispatch.title': 'Dispatch NPC',
  'afk.dispatch.npc': 'NPC',
  'afk.dispatch.biome': 'Biome',
  'afk.dispatch.duration': 'Duration',
  'afk.dispatch.short': '1 hour',
  'afk.dispatch.medium': '4 hours',
  'afk.dispatch.long': '12 hours',
  'afk.dispatch.start': 'Dispatch',
  'afk.dispatch.cancel': 'Cancel',
  'afk.claim.title': 'Rewards ready!',
  'afk.claim.body': 'Your NPCs returned with items.',
  'afk.claim.confirm': 'Claim all',
  'afk.status.active': 'In progress',
  'afk.status.claimable': 'Ready',
  'memorial.npc.title': 'Memorial',
  'memorial.npc.died': 'Died',
  'memorial.npc.cause': 'Cause',
  'tree.stage.sapling': 'Sapling',
  'tree.stage.small': 'Small',
  'tree.stage.adult': 'Adult',
  'tree.stage.ancient': 'Ancient',
  'bestiary.enemies.title': 'Enemies',
  'bestiary.enemies.kills': 'Kills: {n}',
  'bestiary.enemies.unknown': '???',
  'bestiary.fauna.title': 'Fauna',
  'bestiary.wild.title': 'Wild populations',
  'bestiary.wild.dominant': 'Dominant',
  'bestiary.wild.encounters': '{n} encounters',
  'bestiary.empty': 'No records yet.',
  'bestiary.tab.types': 'Types',
  'bestiary.tab.enemies': 'Enemies',
  'cloud.title': 'Cloud Save',
  'cloud.enable': 'Enable cloud sync',
  'cloud.unavailable': 'Cloud unavailable in this build (env not configured).',
  'cloud.email.placeholder': 'your@email.com',
  'cloud.magicLink.send': 'Send login link',
  'cloud.magicLink.sent': 'Link sent! Check your email.',
  'cloud.loggedAs': 'Logged as',
  'cloud.status': 'Status',
  'cloud.status.idle': 'Idle',
  'cloud.status.syncing': 'Syncing…',
  'cloud.status.synced': 'Synced',
  'cloud.status.error': 'Error',
  'cloud.status.disabled': 'Disabled',
  'cloud.lastSync': 'Last sync',
  'cloud.syncNow': 'Sync now',
  'cloud.signOut': 'Sign out',
  'cloud.signedOut': 'Signed out.',
  'cloud.synced': 'Save synced!',
  'cloud.error': 'Error',
  'onboarding.title': 'Welcome to Himotchi!',
  'onboarding.s1': 'Take care of your pet! Use the buttons at the bottom to feed, play and clean.',
  'onboarding.s2': 'When it grows up, it can explore the world. Watch the environment — vitality drops!',
  'onboarding.s3': 'In expedition, defeat enemies for mutations. When it becomes adult, breed with other creatures to start the next generation!',
  'onboarding.next': 'Next',
  'onboarding.done': 'Let\'s go!',
  'onboarding.skip': 'Skip',
  'a11y.colorblind': 'Colorblind mode',
  'a11y.colorblind.off': 'Off',
  'a11y.colorblind.deutero': 'Deuteranopia',
};
